// Shared TypeScript interfaces for the Adherence Analysis feature
import moment from 'moment';

export interface SentimentSnapshot {
  emotion: string;
  confidence: number;
}

export interface TranscriptSnippet {
  text: string;
  speaker: string;
  participantId?: string;
  speakerName?: string;
  timestamp: string;
}

export interface ObjFaqItem {
  question: string;
  category: string;
  timestamp: string;
  timestamp_iso?: string;
  stored_answer?: string;
  ai_answer?: string;
  handling_score: number;
  adherence_label: 'followed' | 'partial' | 'deviated' | 'not_addressed';
  adherence_summary: string;
  what_worked?: string;
  what_missed?: string;
  what_rep_should_have_said?: string;
  customer_reaction?: string;
  sentiment_before?: SentimentSnapshot;
  sentiment_after?: SentimentSnapshot;
  sentiment_trend?: 'improved' | 'stable' | 'declined' | 'unknown';
  relevant_transcripts?: TranscriptSnippet[];
}

export interface LandmineItem {
  trigger: string;
  timestamp: string;
  timestamp_iso?: string;
  priority: string;
  kb_can_say?: string[];
  kb_cannot_say?: string[];
  kb_quick_tips?: string;
  triggered_by: string;
  handling_score: number;
  handling_label: 'well_handled' | 'partially_handled' | 'not_handled' | 'worsened';
  handling_summary: string;
  what_worked?: string;
  what_missed?: string;
  what_rep_should_have_said?: string;
  customer_reaction?: string;
  sentiment_at_detection?: SentimentSnapshot;
  sentiment_after?: SentimentSnapshot;
  relevant_transcripts?: TranscriptSnippet[];
}

export interface CompetitorItem {
  competitor: string;
  timestamp: string;
  timestamp_iso?: string;
  kb_advantages?: string[];
  kb_weaknesses?: string[];
  kb_talking_points?: string;
  triggered_by: string;
  handling_score: number;
  handling_label: 'used_playbook' | 'partial' | 'ignored';
  handling_summary: string;
  what_worked?: string;
  what_missed?: string;
  what_rep_should_have_said?: string;
  customer_reaction?: string;
  sentiment_at_detection?: SentimentSnapshot;
  sentiment_after?: SentimentSnapshot;
  relevant_transcripts?: TranscriptSnippet[];
}

export interface GenericPhaseAnalysis {
  score: number;
  summary: string;
  what_worked: string;
  what_missed: string;
  coaching: string;
}

export interface PlaybookAdherence {
  was_used: boolean;
  overall_score: number;
  overall_summary: string;
  intro_coverage?: {
    total_available: number;
    total_delivered: number;
    coverage_pct: number;
    missed_intro_points: string[];
    intro_quality_summary: string;
  } | null;
  question_coverage?: {
    total_available: number;
    total_asked: number;
    coverage_pct: number;
    missed_questions: string[];
    question_quality_summary: string;
  } | null;
  deal_progression_coverage?: {
    total_available: number;
    total_delivered: number;
    coverage_pct: number;
    missed_progression_points: string[];
    progression_summary: string;
  } | null;
  intro_generic?: GenericPhaseAnalysis;
  question_generic?: GenericPhaseAnalysis;
  deal_progression_generic?: GenericPhaseAnalysis;
  recommendations: string[];
  playbook_impact_narrative?: string | null;
}

export interface RapportMoment {
  timestamp_approx: string;
  description: string;
}

// --- Sales Intelligence v2 (phase-based) ---

export interface PhaseQuote {
  speaker: string;
  text: string;
  timestamp_approx?: string;
}

export interface CallPhaseAnalysis {
  phase: 'opener' | 'discovery' | 'pitch' | 'objection' | 'close_attempt' | 'wrap_up';
  primary_timestamp?: string;
  start_approx?: string;
  end_approx?: string;
  buying_signal: string;
  buying_signal_score: number;
  emotional_tone: string;
  emotional_tone_score: number;
  trigger: string;
  conversation_control: string;
  supporting_quotes: PhaseQuote[];
  phase_summary: string;
  what_worked: string;
  what_didnt_work: string;
}

export interface SalesIntelligence {
  phases: CallPhaseAnalysis[];
  overall_session_signal: string;
  overall_session_signal_score: number;
  closing_signal: string;
  closing_signal_score: number;
  phase_narrative: string;
  deal_risk_signals: string[];
  rapport_moments: RapportMoment[];
  coaching_for_this_client: string[];
  coaching_for_general_improvement: string[];
  speech_rate_assessment?: string;
}

export interface ManagerReview {
  reviewed_by: string;
  reviewed_by_email: string;
  reviewed_at: string;
  section_comments: {
    objection_faq?: string;
    landmine?: string;
    competitor?: string;
    playbook?: string;
    sales_intelligence?: string;
  };
}

export interface SpeakerStats {
  rep_word_count: number;
  customer_word_count: number;
  rep_pct: number;
  customer_pct: number;
  rep_turn_count: number;
  customer_turn_count: number;
}

export interface ToneScore {
  /** 0-100: how strongly this tone quality was present in the rep's speech. */
  score: number;
  /** 0-100: how confident the AI is in this particular score. */
  confidence_level: number;
  /** Specific language patterns that justify the score. */
  explanation: string;
}

export interface ToneAnalysis {
  positive: {
    confidence: ToneScore;
    optimism: ToneScore;
  };
  negative: {
    defensiveness: ToneScore;
    confrontation: ToneScore;
  };
  overall_coaching: string;
}

export interface CallInsightsAnalysis {
  status: 'pending' | 'complete' | 'failed';
  error?: string;
  generated_at?: string;
  model_info?: { provider: string; model: string };
  /** What queued this run. 'manual' = admin re-analyze, 'reweight' = admin
   *  changed this call's rubric weights (only the affected dimensions re-run).
   *  Absent for an automatic post-call run. */
  triggered_by?: 'manual' | 'reweight' | string;
  /** Dimension keys pushed by the service as each parallel task finishes. */
  dimensions_complete?: string[];
  speaker_stats?: SpeakerStats;
  objection_faq?: {
    overall_summary: string;
    items: ObjFaqItem[];
  };
  landmine?: {
    overall_summary: string;
    items: LandmineItem[];
  };
  competitor?: {
    overall_summary: string;
    items: CompetitorItem[];
  };
  playbook?: PlaybookAdherence;
  sales_intelligence?: SalesIntelligence;
  tone_analysis?: ToneAnalysis;
  manager_review?: ManagerReview;
  /** Admin-edited coaching overrides keyed by sectionKey (e.g. 'speed', 'tone'). */
  coaching_overrides?: Record<string, string[]>;
  /** 5 LLM-synthesized bullet points summarizing the full call (one per level). */
  call_summary_bullets?: string[];
  /** Lushin Discovery Call Rubric result (replaces the 6 dimensions above for
   *  the post-call-only configuration). */
  rubricAnalysis?: RubricAnalysis;
}

/** One timestamped evidence reference for a rubric category. */
export interface RubricEvidence {
  quote?: string;
  timestamp?: string;
  timestamp_iso?: string;
}

/** One "what went well" bullet, with the moment that proves it. */
export interface RubricStrength {
  point?: string;
  /** Absent when nothing quotable supports the point (or on a no-quotes re-run). */
  evidence?: RubricEvidence;
}

/** One "the fix" bullet, with the words the rep could have used instead. */
export interface RubricFix {
  point?: string;
  /** Spoken language, grounded in the Lushin playbook where a line fit. */
  suggested_language?: string;
  /** Label of the playbook line it came from. Empty when the model wrote it. */
  playbook_reference?: string;
}

export interface RubricCategoryResult {
  key?: string;
  number?: number;
  title?: string;
  weight?: number;
  raw_score?: number | null;
  weighted_score?: number | null;
  /** Multiple timestamped evidence references (1-5 per category). */
  evidence?: RubricEvidence[];
  /** Bullet shape. Absent on calls graded before bullets existed — the modal
   *  falls back to the prose fields below, which are still written (derived
   *  from these) for every new call. */
  summary_points?: string[];
  strengths?: RubricStrength[];
  fixes?: RubricFix[];
  what_went_well?: string;
  what_was_missed?: string;
  justification?: string;
  error?: string;
}

export interface RubricAnalysis {
  rubric?: string;
  meetingType?: string | null;
  categories?: RubricCategoryResult[];
  scored_count?: number;
  category_count?: number;
  raw_total?: number;
  raw_max?: number;
  weighted_total?: number;
  weighted_max?: number;
  percentage?: number;
  band?: 'standard' | 'strong' | 'soft' | 'at_risk' | 'lost' | string;
  band_label?: string;
  band_interpretation?: string;
  /** True when an admin retuned this call's weights away from equal weighting. */
  isCustomWeighted?: boolean;
  /** What this call would have scored at equal weights — keeps a reweighted
   *  score honest by showing what the weighting actually changed. */
  equal_weight_percentage?: number;
  equal_weight_band?: string;
  equal_weight_band_label?: string;
  /** Provenance for the "Custom weights" badge. Absent until someone retunes. */
  weighting?: {
    adjustedBy?: string;
    adjustedByName?: string;
    adjustedAt?: string;
    hasCustomInstruction?: boolean;
  };
  narrative?: {
    highest_leverage_strength?: string;
    highest_leverage_fix?: string;
    where_the_deal_stands?: string;
    /** Only produced for custom-weighted calls. */
    weighting_rationale?: string;
    scoring_approach?: string;
  };
}

/** Per-call rubric weighting, as stored on the session and sent by the editor. */
export interface RubricWeighting {
  weights?: Record<string, number>;
  customInstruction?: string;
  lockedKeys?: string[];
  adjustedBy?: string;
  adjustedByName?: string;
  adjustedAt?: string;
}

export type CallInsightsSection =
  | 'objection_faq'
  | 'landmine'
  | 'competitor'
  | 'playbook'
  | 'sales_intelligence';

// --- Shared helper functions ---

/**
 * Formats an ISO timestamp string as "Mar 9th 2026 11:46 AM".
 * Naive timestamps (no Z / offset) are treated as UTC, matching how the
 * Python backend stores them.
 */
export function formatCallInsightsTimestamp(ts: string | undefined | null): string {
  if (!ts) return '';
  // Append Z if no timezone info so moment treats it as UTC, not local time.
  const normalized = !ts.endsWith('Z') && !ts.includes('+') ? `${ts}Z` : ts;
  const m = moment(normalized);
  return m.isValid() ? m.format('MMM Do YYYY h:mm A') : ts;
}

/**
 * Returns Tailwind classes for a 0-100 numeric score badge.
 */
export function getScoreBadgeClasses(score: number): string {
  if (score >= 80) return 'bg-green-100 text-green-800 border border-green-300';
  if (score >= 50) return 'bg-yellow-100 text-yellow-800 border border-yellow-300';
  return 'bg-red-100 text-red-800 border border-red-300';
}

/**
 * Returns Tailwind classes for adherence / handling label badges.
 */
export function getAdherenceLabelClasses(label: string): string {
  switch (label) {
    case 'followed':
    case 'well_handled':
    case 'used_playbook':
      return 'bg-green-100 text-green-800 border border-green-300';
    case 'partial':
    case 'partially_handled':
      return 'bg-yellow-100 text-yellow-800 border border-yellow-300';
    case 'deviated':
    case 'not_handled':
    case 'ignored':
    case 'worsened':
    case 'not_addressed':
    default:
      return 'bg-red-100 text-red-800 border border-red-300';
  }
}

/**
 * Human-readable label display names.
 */
export function formatAdherenceLabel(label: string): string {
  const labelMap: Record<string, string> = {
    followed: 'Followed',
    partial: 'Partial',
    deviated: 'Deviated',
    not_addressed: 'Not Addressed',
    well_handled: 'Well Handled',
    partially_handled: 'Partially Handled',
    not_handled: 'Not Handled',
    worsened: 'Worsened',
    used_playbook: 'Used Playbook',
    ignored: 'Ignored',
  };
  return labelMap[label] ?? label;
}

/**
 * Returns a text indicator for sentiment trend.
 */
export function getSentimentTrendLabel(trend?: string): string {
  switch (trend) {
    case 'improved': return '[+] Improved';
    case 'stable': return '[=] Stable';
    case 'declined': return '[-] Declined';
    default: return '[?] Unknown';
  }
}

export function getSentimentTrendClasses(trend?: string): string {
  switch (trend) {
    case 'improved': return 'text-green-700';
    case 'stable': return 'text-gray-600';
    case 'declined': return 'text-red-700';
    default: return 'text-gray-400';
  }
}

/**
 * Returns Tailwind classes for coverage percentage progress bars.
 */
export function getCoveragePctClasses(pct: number): string {
  if (pct >= 80) return 'bg-green-500';
  if (pct >= 50) return 'bg-yellow-500';
  return 'bg-red-500';
}

/**
 * Returns Tailwind classes for landmine priority badges (high / medium / low).
 */
export function getPriorityClasses(priority: string): string {
  switch (priority?.toLowerCase()) {
    case 'high': return 'bg-red-100 text-red-800 border border-red-300';
    case 'medium': return 'bg-yellow-100 text-yellow-800 border border-yellow-300';
    default: return 'bg-gray-100 text-gray-700 border border-gray-300';
  }
}

/**
 * Returns Tailwind classes for customer disposition badges.
 */
export function getDispositionClasses(disposition: string): string {
  switch (disposition?.toLowerCase()) {
    case 'positive':
    case 'highly_interested': return 'bg-green-100 text-green-800 border border-green-300';
    case 'negative':
    case 'resistant':
    case 'disengaged': return 'bg-red-100 text-red-800 border border-red-300';
    case 'interested': return 'bg-blue-100 text-blue-800 border border-blue-300';
    case 'hesitant':
    case 'skeptical':
    case 'neutral': return 'bg-yellow-100 text-yellow-800 border border-yellow-300';
    default: return 'bg-gray-100 text-gray-700 border border-gray-300';
  }
}

/**
 * Returns Tailwind classes for signal strength badges (strong / medium / weak).
 */
export function getStrengthClasses(strength: string): string {
  switch (strength?.toLowerCase()) {
    case 'strong': return 'bg-green-100 text-green-800 border border-green-300';
    case 'medium': return 'bg-yellow-100 text-yellow-800 border border-yellow-300';
    default: return 'bg-gray-100 text-gray-600 border border-gray-300';
  }
}

/**
 * Returns Tailwind text-colour classes for the engagement arc trend.
 */
export function getArcTrendClass(trend: string): string {
  switch (trend?.toLowerCase()) {
    case 'improving': return 'text-green-700';
    case 'declining': return 'text-red-700';
    case 'mixed': return 'text-yellow-700';
    default: return 'text-gray-600';
  }
}

// --- Sales Intelligence v2 helpers ---

const PHASE_LABELS: Record<string, string> = {
  opener: 'Opener',
  discovery: 'Discovery',
  pitch: 'Pitch',
  objection: 'Objection Handling',
  close_attempt: 'Close Attempt',
  wrap_up: 'Wrap-Up',
};

export function getPhaseLabel(phase: string): string {
  return PHASE_LABELS[phase] ?? phase;
}

export function getBuyingSignalColor(signal: string): string {
  switch (signal?.toLowerCase()) {
    case 'ready_to_commit': return 'bg-green-100 text-green-800 border border-green-300';
    case 'interested':
    case 'curious': return 'bg-blue-100 text-blue-800 border border-blue-300';
    case 'evaluating': return 'bg-indigo-100 text-indigo-800 border border-indigo-300';
    case 'hesitant': return 'bg-yellow-100 text-yellow-800 border border-yellow-300';
    case 'resistant':
    case 'objecting': return 'bg-red-100 text-red-800 border border-red-300';
    case 'disengaged': return 'bg-gray-200 text-gray-700 border border-gray-400';
    default: return 'bg-gray-100 text-gray-700 border border-gray-300';
  }
}

// --- Team Coaching Priorities (Dashboard aggregation) ---

export interface CallInsightsStatsItem {
  _id: string;
  avg_score: number;
  count: number;
}

export interface CallInsightsStats {
  weakest_objections: CallInsightsStatsItem[];
  weakest_faqs: CallInsightsStatsItem[];
  hardest_competitors: CallInsightsStatsItem[];
  hardest_landmines: CallInsightsStatsItem[];
  closing_signals: Array<{ _id: string; count: number }>;
  total_analyzed_sessions: number;
  playbook_coverage: {
    avg_overall: number;
    avg_intro: number;
    avg_questions: number;
    avg_deal_progression: number;
    session_count: number;
  } | null;
}

export function getConversationControlColor(control: string): string {
  switch (control?.toLowerCase()) {
    case 'balanced': return 'bg-green-100 text-green-800 border border-green-300';
    case 'rep_leading': return 'bg-blue-100 text-blue-800 border border-blue-300';
    case 'prospect_leading': return 'bg-indigo-100 text-indigo-800 border border-indigo-300';
    case 'prospect_deflecting': return 'bg-yellow-100 text-yellow-800 border border-yellow-300';
    case 'rep_overtalking': return 'bg-red-100 text-red-800 border border-red-300';
    default: return 'bg-gray-100 text-gray-700 border border-gray-300';
  }
}

export function getEmotionalToneColor(tone: string): string {
  switch (tone?.toLowerCase()) {
    case 'enthusiastic':
    case 'confident': return 'bg-green-100 text-green-800 border border-green-300';
    case 'comfortable':
    case 'relieved': return 'bg-blue-100 text-blue-800 border border-blue-300';
    case 'skeptical':
    case 'anxious': return 'bg-yellow-100 text-yellow-800 border border-yellow-300';
    case 'frustrated':
    case 'pressured': return 'bg-red-100 text-red-800 border border-red-300';
    case 'confused': return 'bg-orange-100 text-orange-800 border border-orange-300';
    case 'indifferent': return 'bg-gray-200 text-gray-700 border border-gray-400';
    default: return 'bg-gray-100 text-gray-700 border border-gray-300';
  }
}
